import { filter, groupBy, keyBy, map, orderBy } from "lodash-es";

import type {
  AssistantFollowUpObservable,
  AssistantProjectObservable,
  AssistantTaskObservable,
} from "@/store/assistant";
import { isClosedAssistantTaskStatus } from "@/store/assistant/assistant-task-status";
// Imported from the defining module rather than the barrel: the barrel pulls in
// the sync-model classes, which cycle back through this module's importers.
import { AssistantTaskStatus } from "@/store/assistant/types";
import { getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import { buildAgentSearchableText } from "@/pages/agent/utils/agent-search.utils";
import {
  resolveTaskSourceNotes,
  type TaskSourceNote,
  type TaskSourceReference,
} from "@/pages/agent/utils/agent-task-source.utils";
import { TaskView } from "@/pages/agent/tasks/use-agent-tasks-search-params";

export type TaskRowViewModel = {
  task: AssistantTaskObservable;
  createdAtTimestamp: number;
  dueAtTimestamp: number;
  updatedAtTimestamp: number;
  project: AssistantProjectObservable | undefined;
  projectTitle: string;
  sourceNote: TaskSourceNote | undefined;
  nextFollowUp: AssistantFollowUpObservable | undefined;
  lastFollowUp: AssistantFollowUpObservable | undefined;
  isClosed: boolean;
  searchableText: string;
};

export const DEFAULT_TASK_STATUS_FILTER_VALUES = [
  AssistantTaskStatus.Pending,
  AssistantTaskStatus.InProgress,
  AssistantTaskStatus.Completed,
  AssistantTaskStatus.Canceled,
];

const getNextFollowUp = ({ followUps }: { followUps: AssistantFollowUpObservable[] }) => {
  const pendingFollowUps = filter(followUps, followUp => !isClosedFollowUp({ followUp }));

  return orderBy(
    pendingFollowUps,
    followUp =>
      getAgentTimestamp({
        value: followUp.scheduledFor ?? followUp.dueAt,
        fallback: Number.POSITIVE_INFINITY,
      }),
    "asc"
  )[0];
};

const getLastFollowUp = ({ followUps }: { followUps: AssistantFollowUpObservable[] }) =>
  orderBy(
    followUps,
    followUp =>
      getAgentTimestamp({
        value:
          followUp.completedAt ?? followUp.firedAt ?? followUp.scheduledFor ?? followUp.updatedAt,
        fallback: 0,
      }),
    "desc"
  )[0];

/**
 * Combines task, project, follow-up, and note-reference models into rows for the tasks view.
 */
export const buildTaskRows = ({
  tasks,
  projects,
  followUps,
  taskNoteReferences,
  getNoteById,
}: {
  tasks: AssistantTaskObservable[];
  projects: AssistantProjectObservable[];
  followUps: AssistantFollowUpObservable[];
  taskNoteReferences: TaskSourceReference[];
  getNoteById: ({
    noteId,
  }: {
    noteId: TaskSourceReference["modelData"]["note_id"];
  }) => TaskSourceNote | undefined;
}) => {
  const projectsById = keyBy(projects, project => project.id);
  const followUpsWithTask = filter(followUps, followUp => Boolean(followUp.taskId));
  const followUpsByTaskId = groupBy(followUpsWithTask, followUp => followUp.taskId ?? "");
  const taskNoteReferencesByTaskId = groupBy(
    taskNoteReferences,
    reference => reference.modelData.task_id
  );

  return map(tasks, task => {
    const project = task.projectId ? projectsById[task.projectId] : undefined;
    const projectTitle = project?.title ?? "No project";
    const taskFollowUps = followUpsByTaskId[task.id] ?? [];
    const taskReferences = taskNoteReferencesByTaskId[task.id] ?? [];
    const title = task.title || "Untitled task";
    const sourceNote = resolveTaskSourceNotes({
      references: taskReferences,
      getNoteById,
    })[0];

    return {
      task,
      createdAtTimestamp: getAgentTimestamp({ value: task.createdAt, fallback: 0 }),
      dueAtTimestamp: getAgentTimestamp({
        value: task.dueAt,
        fallback: Number.POSITIVE_INFINITY,
      }),
      updatedAtTimestamp: getAgentTimestamp({ value: task.updatedAt, fallback: 0 }),
      project,
      projectTitle,
      sourceNote,
      nextFollowUp: getNextFollowUp({ followUps: taskFollowUps }),
      lastFollowUp: getLastFollowUp({ followUps: taskFollowUps }),
      isClosed: isClosedAssistantTaskStatus({ status: task.status }),
      searchableText: buildAgentSearchableText({
        values: [title, task.description, sourceNote?.title, projectTitle, task.status],
      }),
    } satisfies TaskRowViewModel;
  });
};

const AGENT_TASK_FOCUS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const getTaskCreatedAt = (taskRow: TaskRowViewModel) => taskRow.createdAtTimestamp;

const getTaskUpdatedAt = (taskRow: TaskRowViewModel) => taskRow.updatedAtTimestamp;

const getTaskDueAt = (taskRow: TaskRowViewModel) => taskRow.dueAtTimestamp;

const isWithinWindow = ({
  timestamp,
  start,
  end,
}: {
  timestamp: number;
  start: number;
  end: number;
}) => timestamp >= start && timestamp <= end;

const getTopOfMindBucket = ({
  taskRow,
  currentTime,
  recentStart,
  dueSoonEnd,
}: {
  taskRow: TaskRowViewModel;
  currentTime: number;
  recentStart: number;
  dueSoonEnd: number;
}) => {
  const dueAt = getTaskDueAt(taskRow);

  if (isWithinWindow({ timestamp: dueAt, start: recentStart, end: dueSoonEnd })) {
    return 0;
  }

  if (
    isWithinWindow({
      timestamp: getTaskUpdatedAt(taskRow),
      start: recentStart,
      end: currentTime,
    })
  ) {
    return 1;
  }

  if (
    isWithinWindow({
      timestamp: getTaskCreatedAt(taskRow),
      start: recentStart,
      end: currentTime,
    })
  ) {
    return 2;
  }

  return 3;
};

/**
 * Builds the task surface's focused views around a single seven-day window.
 *
 * Focused views intentionally contain open work only. All tasks is the escape
 * hatch for completed or older work and uses newest-created ordering so stale
 * due dates cannot dominate the general list. Top of mind puts due work first
 * by absolute distance from now, so upcoming and recently overdue tasks share
 * one urgency timeline.
 */
export const getTaskRowsForView = ({
  taskRows,
  view,
  currentTime = Date.now(),
}: {
  taskRows: TaskRowViewModel[];
  view: TaskView;
  currentTime?: number;
}) => {
  const recentStart = currentTime - AGENT_TASK_FOCUS_WINDOW_MS;
  const dueSoonEnd = currentTime + AGENT_TASK_FOCUS_WINDOW_MS;

  if (view === TaskView.AllTasks) {
    return orderBy(taskRows, [getTaskCreatedAt, getTaskUpdatedAt], ["desc", "desc"]);
  }

  const openTaskRows = filter(taskRows, taskRow => !taskRow.isClosed);

  if (view === TaskView.RecentlyAdded) {
    return orderBy(
      filter(openTaskRows, taskRow =>
        isWithinWindow({
          timestamp: getTaskCreatedAt(taskRow),
          start: recentStart,
          end: currentTime,
        })
      ),
      getTaskCreatedAt,
      "desc"
    );
  }

  if (view === TaskView.RecentlyUpdated) {
    return orderBy(
      filter(openTaskRows, taskRow =>
        isWithinWindow({
          timestamp: getTaskUpdatedAt(taskRow),
          start: recentStart,
          end: currentTime,
        })
      ),
      getTaskUpdatedAt,
      "desc"
    );
  }

  if (view === TaskView.DueSoon) {
    return orderBy(
      filter(openTaskRows, taskRow =>
        isWithinWindow({
          timestamp: getTaskDueAt(taskRow),
          start: currentTime,
          end: dueSoonEnd,
        })
      ),
      getTaskDueAt,
      "asc"
    );
  }

  if (view === TaskView.RecentlyOverdue) {
    return orderBy(
      filter(openTaskRows, taskRow =>
        isWithinWindow({
          timestamp: getTaskDueAt(taskRow),
          start: recentStart,
          end: currentTime,
        })
      ),
      getTaskDueAt,
      "desc"
    );
  }

  const topOfMindTaskRows = filter(openTaskRows, taskRow => {
    const dueAt = getTaskDueAt(taskRow);
    const isDueSoonOrRecentlyOverdue = isWithinWindow({
      timestamp: dueAt,
      start: recentStart,
      end: dueSoonEnd,
    });
    const isRecentlyCreated = isWithinWindow({
      timestamp: getTaskCreatedAt(taskRow),
      start: recentStart,
      end: currentTime,
    });
    const isRecentlyUpdated = isWithinWindow({
      timestamp: getTaskUpdatedAt(taskRow),
      start: recentStart,
      end: currentTime,
    });

    return isDueSoonOrRecentlyOverdue || isRecentlyCreated || isRecentlyUpdated;
  });

  return orderBy(
    topOfMindTaskRows,
    [
      taskRow =>
        getTopOfMindBucket({
          taskRow,
          currentTime,
          recentStart,
          dueSoonEnd,
        }),
      taskRow => Math.abs(getTaskDueAt(taskRow) - currentTime),
      getTaskUpdatedAt,
      getTaskCreatedAt,
    ],
    ["asc", "asc", "desc", "desc"]
  );
};
